import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type {
  VerdaCostLedgerEntry,
  VerdaDestroyOutcome,
  VerdaFinding,
  VerdaGpuInvoice,
  VerdaGpuRefundRequest,
  VerdaGpuServer,
  VerdaGpuServerList,
  VerdaGpuServerUsageRow,
} from '../types/models/Verda';
import type {
  ApiResponseVerdaCostLedger,
  ApiResponseVerdaFindings,
  ApiResponseVerdaGpuDestroy,
  ApiResponseVerdaGpuInvoice,
  ApiResponseVerdaGpuInvoiceRelease,
  ApiResponseVerdaGpuInvoices,
  ApiResponseVerdaGpuMarkNotCreated,
  ApiResponseVerdaGpuRefund,
  ApiResponseVerdaGpuResolve,
  ApiResponseVerdaGpuServer,
  ApiResponseVerdaGpuServers,
} from '../types/api/ApiResponseVerda';
import { isAbortError, jsendFieldErrors, readJsendBody } from './gpuServerResponse';
import type { GpuJsendEnvelope } from './gpuServerResponse';

// Staff-only hooks over the "GPU servers (staff)" section of docs/api-types/admin-verda.md. Mount them
// behind the `is_ancalagon_staff` gate. Unlike the live Verda reads of useAdminVerda.ts (fetch-once),
// these routes read the DATABASE only, so they are refetchable: a read runs when enabled, again
// whenever its query (filters) changes, and on `refetch` — but never because `apiFetch` changed
// identity on an OIDC silent renew (the latest `apiFetch` is read through a ref). No auto-polling:
// the staff tabs refresh manually.

/** Client-side filtering needs the whole bounded set: ask for the backend's maximum. */
export const ADMIN_GPU_LIST_LIMIT = 500;

/** The router's `fail` (no `message`) for a GPU staff route: the backend doesn't have it deployed. */
const STAFF_ROUTE_MISSING_MESSAGE = 'The GPU server routes are not available on this backend yet.';

/**
 * The message to show for a non-success staff body. A validation error ("Request contains errors")
 * is spelled out with its `data.<field>` messages, since the bare message says nothing.
 */
export function staffErrorMessage(body: GpuJsendEnvelope | null, fallback: string): string {
  const fieldMessages = Object.values(jsendFieldErrors(body));
  if (typeof body?.message === 'string' && body.message !== '') {
    return body.message === 'Request contains errors' && fieldMessages.length > 0
      ? `${body.message}: ${fieldMessages.join('; ')}`
      : body.message;
  }
  if (body?.status === 'fail') {
    return STAFF_ROUTE_MISSING_MESSAGE;
  }
  return fallback;
}

interface StaffReadState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** The detail read's `GPU server not found`. */
  notFound: boolean;
  refetch: () => Promise<void>;
}

/**
 * Fetch `path` when enabled (and again when `path` changes), keeping `pick(body)` of a JSend success;
 * `pick` returns `null` for a success missing its keys, reported as an error. Pass a module-level
 * `pick` so its identity is stable. `path === null` = nothing to fetch.
 */
function useStaffGpuRead<R extends GpuJsendEnvelope, T>(
  path: string | null,
  pick: (body: R) => T | null,
  what: string,
  enabled: boolean,
): StaffReadState<T> {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  useEffect(() => {
    apiFetchRef.current = apiFetch;
  }, [apiFetch]);

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(enabled && path !== null);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const fetchedPathRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (path === null) {
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetchRef.current(path, { signal: controller.signal });
      const body = await readJsendBody<R>(response);
      if (controller.signal.aborted) {
        return;
      }
      const picked = body?.status === 'success' ? pick(body) : null;
      if (picked !== null) {
        setData(picked);
        setNotFound(false);
        return;
      }
      if (body?.status === 'error' && body.message === 'GPU server not found') {
        setData(null);
        setNotFound(true);
        return;
      }
      setError(staffErrorMessage(body, `Could not load the ${what}.`));
    } catch (err) {
      if (isAbortError(err, controller.signal)) {
        return;
      }
      setError(err instanceof Error ? err.message : `An error occurred while loading the ${what}.`);
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
        setLoading(false);
      }
    }
  }, [path, pick, what]);

  useEffect(() => {
    if (!enabled || !auth.isAuthenticated || path === null || fetchedPathRef.current === path) {
      return;
    }
    // A new query: drop the previous result so the tab shows its spinner, not stale rows.
    const isNewQuery = fetchedPathRef.current !== null;
    fetchedPathRef.current = path;
    if (isNewQuery) {
      setData(null);
      setNotFound(false);
    }
    void load();
  }, [enabled, auth.isAuthenticated, path, load]);

  // Forgetting the fetched path makes a remount read again — StrictMode's development remount included,
  // whose cleanup aborts the first read (without the reset, nothing would ever re-issue it).
  useEffect(
    () => () => {
      abortRef.current?.abort();
      fetchedPathRef.current = null;
    },
    [],
  );

  return { data, loading, error, notFound, refetch: load };
}

// ---- Reads --------------------------------------------------------------------------------------

export interface AdminGpuServerFilters {
  /** Internal statuses; `[]` = every status. */
  statuses: string[];
  /** Only rows with attention items (`attention=1`). */
  attentionOnly: boolean;
}

/** The query string of `GET /admin/verda/servers` (exported for tests). */
export function adminGpuServersPath(filters: AdminGpuServerFilters): string {
  const params = new URLSearchParams();
  if (filters.statuses.length > 0) {
    params.set('status', filters.statuses.join(','));
  }
  if (filters.attentionOnly) {
    params.set('attention', '1');
  }
  params.set('limit', String(ADMIN_GPU_LIST_LIMIT));
  return `/admin/verda/servers?${params.toString()}`;
}

const pickServers = (body: ApiResponseVerdaGpuServers): VerdaGpuServerList | null =>
  Array.isArray(body.data?.servers)
    ? { servers: body.data.servers, namespace: typeof body.data.namespace === 'string' ? body.data.namespace : null }
    : null;

/** `GET /admin/verda/servers` — every client's GPU servers, newest first (no row actions). */
export const useAdminGpuServers = (filters: AdminGpuServerFilters, enabled: boolean = true) => {
  const { data, ...rest } = useStaffGpuRead(adminGpuServersPath(filters), pickServers, 'GPU servers', enabled);
  return { servers: data?.servers ?? null, namespace: data?.namespace ?? null, ...rest };
};

export interface AdminGpuServerDetail {
  server: VerdaGpuServer;
  /** The hour rows (capped at 200, newest first) — distinct from `server.usage`, the summary. */
  usage: VerdaGpuServerUsageRow[];
  /** Its `subject_type: 'server'` notices (customer `destroyed` notice + staff findings). */
  notices: VerdaFinding[];
}

const pickServerDetail = (body: ApiResponseVerdaGpuServer): AdminGpuServerDetail | null =>
  body.data?.server
    ? {
        server: body.data.server,
        usage: Array.isArray(body.data.usage) ? body.data.usage : [],
        notices: Array.isArray(body.data.notices) ? body.data.notices : [],
      }
    : null;

/** `GET /admin/verda/servers/{id}` — pass `null` for a route param that isn't a positive integer. */
export const useAdminGpuServer = (id: number | null) => {
  const { data, ...rest } = useStaffGpuRead(
    id === null ? null : `/admin/verda/servers/${id}`,
    pickServerDetail,
    'GPU server',
    id !== null,
  );
  return {
    server: data?.server ?? null,
    usage: data?.usage ?? null,
    notices: data?.notices ?? null,
    ...rest,
    notFound: id === null || rest.notFound,
  };
};

const pickFindings = (body: ApiResponseVerdaFindings): VerdaFinding[] | null =>
  Array.isArray(body.data?.findings) ? body.data.findings : null;

/** `GET /admin/verda/findings` — the meter's staff findings, newest first. */
export const useAdminGpuFindings = (pendingOnly: boolean, enabled: boolean = true) => {
  const path = `/admin/verda/findings?${pendingOnly ? 'pending=1&' : ''}limit=${ADMIN_GPU_LIST_LIMIT}`;
  const { data, ...rest } = useStaffGpuRead(path, pickFindings, 'GPU findings', enabled);
  return { findings: data, ...rest };
};

const pickInvoices = (body: ApiResponseVerdaGpuInvoices): VerdaGpuInvoice[] | null =>
  Array.isArray(body.data?.invoices) ? body.data.invoices : null;

/** `GET /admin/verda/invoices` — the monthly GPU invoices, newest first. `[]` = every status. */
export const useAdminGpuInvoices = (statuses: string[], enabled: boolean = true) => {
  const params = new URLSearchParams();
  if (statuses.length > 0) {
    params.set('status', statuses.join(','));
  }
  params.set('limit', String(ADMIN_GPU_LIST_LIMIT));
  const { data, ...rest } = useStaffGpuRead(
    `/admin/verda/invoices?${params.toString()}`,
    pickInvoices,
    'GPU invoices',
    enabled,
  );
  return { invoices: data, ...rest };
};

const pickLedger = (body: ApiResponseVerdaCostLedger): VerdaCostLedgerEntry[] | null =>
  Array.isArray(body.data?.entries) ? body.data.entries : null;

/** `GET /admin/verda/cost-ledger` — one entry per meter run, newest first (96 ≈ 8 h). */
export const useAdminGpuCostLedger = (limit: number, enabled: boolean = true) => {
  const { data, ...rest } = useStaffGpuRead(
    `/admin/verda/cost-ledger?limit=${limit}`,
    pickLedger,
    'cost ledger',
    enabled,
  );
  return { entries: data, ...rest };
};

// ---- Mutations ----------------------------------------------------------------------------------

export type AdminGpuOutcome<T> =
  | { status: 'success'; data: T }
  /** `message` is display-ready (a validation error spells out its fields); `fieldErrors` = `data.<field>`. */
  | { status: 'error'; message: string; fieldErrors: Record<string, string> };

/**
 * POST `path` with `body`, keeping `pick(body)` of a JSend success. Never throws.
 */
async function postStaffAction<R extends GpuJsendEnvelope, T>(
  apiFetch: ReturnType<typeof useApiFetch>,
  path: string,
  body: unknown,
  pick: (response: R) => T | null,
  fallback: string,
): Promise<AdminGpuOutcome<T>> {
  try {
    const response = await apiFetch(path, {
      method: 'POST',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    const parsed = await readJsendBody<R>(response);
    const picked = parsed?.status === 'success' ? pick(parsed) : null;
    if (picked !== null) {
      return { status: 'success', data: picked };
    }
    return { status: 'error', message: staffErrorMessage(parsed, fallback), fieldErrors: jsendFieldErrors(parsed) };
  } catch (err) {
    return { status: 'error', message: err instanceof Error ? err.message : fallback, fieldErrors: {} };
  }
}

const pickDestroy = (r: ApiResponseVerdaGpuDestroy) =>
  r.data?.server && typeof r.data.outcome === 'string' ? { outcome: r.data.outcome as VerdaDestroyOutcome } : null;
const pickRefund = (r: ApiResponseVerdaGpuRefund) =>
  typeof r.data?.refunded === 'number' ? { refunded: r.data.refunded } : null;
const pickMarkNotCreated = (r: ApiResponseVerdaGpuMarkNotCreated) =>
  typeof r.data?.refunded === 'number'
    ? { refunded: r.data.refunded, refund_failed: typeof r.data.refund_failed === 'number' ? r.data.refund_failed : 0 }
    : null;
const pickResolve = (r: ApiResponseVerdaGpuResolve) => (r.data?.server ? { resolved: true as const } : null);
const pickInvoice = (r: ApiResponseVerdaGpuInvoice) => (r.data?.invoice ? { invoice: r.data.invoice } : null);
const pickRelease = (r: ApiResponseVerdaGpuInvoiceRelease) =>
  typeof r.data?.released === 'number' ? { released: r.data.released } : null;

/**
 * The staff GPU actions. None throws; each resolves to a discriminated outcome. The server actions'
 * responses carry a server whose usage summary is not computed, so callers refetch the detail
 * afterwards instead of applying it. `busy` is true while any action is in flight.
 */
export const useAdminGpuActions = () => {
  const apiFetch = useApiFetch();
  const [busy, setBusy] = useState(false);

  const run = useCallback(async <T>(action: () => Promise<AdminGpuOutcome<T>>): Promise<AdminGpuOutcome<T>> => {
    setBusy(true);
    try {
      return await action();
    } finally {
      setBusy(false);
    }
  }, []);

  /** `POST /admin/verda/servers/{id}/destroy` — a real, tag-checked delete at Verda (no body). */
  const destroy = useCallback(
    (id: number) =>
      run(() =>
        postStaffAction(
          apiFetch,
          `/admin/verda/servers/${id}/destroy`,
          undefined,
          pickDestroy,
          'Could not destroy the server.',
        ),
      ),
    [apiFetch, run],
  );

  /** `POST /admin/verda/servers/{id}/refund` — `{ all: true }` or `{ from_usage_id }` (a JSON integer). */
  const refund = useCallback(
    (id: number, request: VerdaGpuRefundRequest) =>
      run(() =>
        postStaffAction(
          apiFetch,
          `/admin/verda/servers/${id}/refund`,
          request,
          pickRefund,
          'Could not refund the hours.',
        ),
      ),
    [apiFetch, run],
  );

  /** `POST /admin/verda/servers/{id}/resolve` — one-shot, with a note. */
  const resolve = useCallback(
    (id: number, note: string) =>
      run(() =>
        postStaffAction(
          apiFetch,
          `/admin/verda/servers/${id}/resolve`,
          { note },
          pickResolve,
          'Could not resolve the server.',
        ),
      ),
    [apiFetch, run],
  );

  /** `POST /admin/verda/servers/{id}/mark-not-created` — refunds everything; the row becomes `failed`. */
  const markNotCreated = useCallback(
    (id: number, note: string) =>
      run(() =>
        postStaffAction(
          apiFetch,
          `/admin/verda/servers/${id}/mark-not-created`,
          { note },
          pickMarkNotCreated,
          'Could not mark the server as not created.',
        ),
      ),
    [apiFetch, run],
  );

  /** `POST /admin/verda/invoices/{id}/link` — record the Facturier invoice of an ambiguous create. */
  const linkInvoice = useCallback(
    (id: number, facturierInvoiceId: number) =>
      run(() =>
        postStaffAction(
          apiFetch,
          `/admin/verda/invoices/${id}/link`,
          { facturier_invoice_id: facturierInvoiceId },
          pickInvoice,
          'Could not link the invoice.',
        ),
      ),
    [apiFetch, run],
  );

  /** `POST /admin/verda/invoices/{id}/release` — DELETES the row; the month is re-invoiced. */
  const releaseInvoice = useCallback(
    (id: number, note: string) =>
      run(() =>
        postStaffAction(
          apiFetch,
          `/admin/verda/invoices/${id}/release`,
          { note },
          pickRelease,
          'Could not release the invoice.',
        ),
      ),
    [apiFetch, run],
  );

  /** `POST /admin/verda/invoices/{id}/close` — terminal: the hours are never invoiced again. */
  const closeInvoice = useCallback(
    (id: number, note: string) =>
      run(() =>
        postStaffAction(
          apiFetch,
          `/admin/verda/invoices/${id}/close`,
          { note },
          pickInvoice,
          'Could not close the invoice.',
        ),
      ),
    [apiFetch, run],
  );

  return { destroy, refund, resolve, markNotCreated, linkInvoice, releaseInvoice, closeInvoice, busy };
};
